import isBrowser from '@src/quince-site-common/src/utils/isBrowser'

import { IExperimentLoggingData } from '../experiments.types'

const LOCALSTORAGE_KEY = 'statsigExperimentBuckets'

interface StatsigExperimentBucket {
  experimentName: string
  bucketName: string
  isActive: boolean | undefined
}

export class StatsigExperimentManager {
  exposedBuckets: Record<string, StatsigExperimentBucket>

  constructor() {
    this.exposedBuckets = {}
  }

  initialize() {
    const existingBuckets = this.getExposedBucketsFromLocalStorage()

    existingBuckets.forEach(
      ({ experimentName, bucketName, isActive } = { experimentName: '', bucketName: '', isActive: false }) => {
        if (!experimentName || !bucketName) return
        this.exposedBuckets[experimentName] = {
          experimentName,
          bucketName,
          isActive,
        }
      },
    )
  }

  setExposedBuckets(experimentName: string, groupName: string, isActive: boolean) {
    this.exposedBuckets[experimentName] = {
      experimentName,
      bucketName: groupName,
      isActive,
    }

    this.setExposedBucketsInLocalStorage()
  }

  getExposedBuckets() {
    const experimentBuckets: IExperimentLoggingData[] = []
    Object.entries(this.exposedBuckets).forEach(([experimentName, bucket]) => {
      experimentBuckets.push({
        experimentName,
        bucketName: bucket.bucketName,
        isActive: bucket.isActive,
      })
    })
    return Object.freeze(experimentBuckets)
  }

  getActiveExposedBuckets() {
    return this.getExposedBuckets().filter(bucket => {
      // bucket is active can be undefined for users not exposed to experiment after isActive key was added
      return bucket.isActive ?? true
    })
  }

  removeExposedBuckets(experimentName: string) {
    if (this.exposedBuckets[experimentName]) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete this.exposedBuckets[experimentName]
      this.setExposedBucketsInLocalStorage()
    }
  }

  private setExposedBucketsInLocalStorage() {
    try {
      if (!isBrowser()) return void 0

      const experimentBuckets = this.getExposedBuckets()

      localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(experimentBuckets))
    } catch (error) {
      console.error(`Failed to set Statsig Experiment buckets in Local storage: `, error)
    }
  }

  private getExposedBucketsFromLocalStorage() {
    try {
      if (!isBrowser()) return []

      const existingBuckets = JSON.parse(localStorage.getItem(LOCALSTORAGE_KEY) ?? '[]') as IExperimentLoggingData[]
      if (!Array.isArray(existingBuckets)) {
        return []
      }
      return existingBuckets
    } catch (error) {
      console.error(`Failed to get Statsig Experiment buckets from Local storage: `, error)
      return []
    }
  }
}

export const statsigExperimentManager = new StatsigExperimentManager()
